'use client';

import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import type { MetaUserData } from '../../infrastructure/analytics/utils/types';

const INITIAL_PROFILE_WAIT_MS = 5000;

/** Keeps profile data out of analytics context and invalidates in-flight hashes on identity changes. */
export function useMetaUserData(
  userId: string | undefined,
  email: string | undefined,
  hasAdvertisingConsent: boolean,
  isLoading = false,
) {
  const identity = useMemo(
    () => ({ userId, email, hasAdvertisingConsent, isLoading }),
    [userId, email, hasAdvertisingConsent, isLoading],
  );
  const current = useRef<typeof identity | undefined>(identity);
  const waiters = useRef(new Set<() => void>());
  useLayoutEffect(() => {
    current.current = identity;
    if (!identity.isLoading || !identity.hasAdvertisingConsent) {
      waiters.current.forEach((finish) => finish());
    }
    return () => {
      current.current = undefined;
    };
  }, [identity]);

  useLayoutEffect(
    () => () => {
      waiters.current.forEach((finish) => finish());
    },
    [],
  );

  return useCallback(
    async (waitForInitialProfile = false): Promise<(MetaUserData & { userId: string }) | undefined> => {
      let snapshot = current.current;
      if (waitForInitialProfile && snapshot?.isLoading && snapshot.hasAdvertisingConsent) {
        let finish!: () => void;
        const settled = new Promise<typeof identity | undefined>((resolve) => {
          finish = () => resolve(current.current);
        });
        const timer = setTimeout(finish, INITIAL_PROFILE_WAIT_MS);
        waiters.current.add(finish);
        try {
          snapshot = await settled;
        } finally {
          clearTimeout(timer);
          waiters.current.delete(finish);
        }
      }
      const normalizedEmail = snapshot?.email?.trim().toLowerCase();
      if (
        !snapshot?.hasAdvertisingConsent ||
        current.current !== snapshot ||
        snapshot.isLoading ||
        !snapshot.userId ||
        !/^[1-9]\d*$/.test(snapshot.userId ?? '') ||
        !normalizedEmail ||
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)
      ) {
        return undefined;
      }

      try {
        const bytes = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(normalizedEmail));
        if (current.current !== snapshot) {
          return undefined;
        }
        return {
          userId: snapshot.userId,
          emailSha256: Array.from(new Uint8Array(bytes), (byte) => byte.toString(16).padStart(2, '0')).join(''),
        };
      } catch {
        // Matching is optional; unavailable crypto must not suppress analytics.
        return undefined;
      }
    },
    [],
  );
}
